import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n";
import Seo from "@/components/Seo";
import { CONTACT_EMAIL } from "@/lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 6;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { lang } = useLang();
  const [state, setState] = useState("checking"); // checking | success | error | expired

  const copy = {
    es: {
      checking: "Comprobando tu pago…",
      success: "¡Pago confirmado!",
      successText: "Gracias por descubrir Naguarà. Pronto recibirás tu café fuera de lo común.",
      error: "No pudimos confirmar el pago",
      errorText: "Si te han cobrado, escríbenos y lo resolvemos enseguida:",
      expired: "La sesión de pago expiró",
      back: "Volver al inicio",
    },
    en: {
      checking: "Checking your payment…",
      success: "Payment confirmed!",
      successText: "Thanks for discovering Naguarà. Your out-of-the-ordinary coffee is on its way.",
      error: "We couldn't confirm the payment",
      errorText: "If you were charged, write to us and we will sort it out right away:",
      expired: "The payment session expired",
      back: "Back to home",
    },
  }[lang];

  const poll = useCallback(
    async (sessionId, attempt) => {
      if (attempt >= MAX_ATTEMPTS) {
        setState("error");
        return;
      }
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          clearCart();
          setState("success");
          return;
        }
        if (res.data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(() => poll(sessionId, attempt + 1), 2000);
      } catch (e) {
        console.error("status error", e);
        setState("error");
      }
    },
    [clearCart]
  );

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    poll(sessionId, 0);
  }, [params, poll]);

  const Icon =
    state === "success" ? CheckCircle2 : state === "checking" ? Loader2 : XCircle;
  const color =
    state === "success" ? "text-[#3B2523]" : state === "checking" ? "text-[#3B2523]" : "text-[#3B2523]";

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center px-6">
      <Seo
        title={`${copy[state]} · Naguarà`}
        description={copy.checking}
        path="/checkout/success"
        lang={lang}
        noindex
      />
      <div data-testid="checkout-result" className="text-center max-w-md">
        <Icon
          size={64}
          className={`mx-auto ${color} ${state === "checking" ? "animate-spin" : ""}`}
          strokeWidth={1.5}
        />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#3B2523] mt-8">
          {copy[state]}
        </h1>
        {state === "success" && <p className="text-[#3B2523]/70 mt-4 leading-relaxed">{copy.successText}</p>}
        {state === "error" && (
          <p className="text-[#3B2523]/70 mt-4 leading-relaxed">
            {copy.errorText}{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="checkout-error-mailto"
              className="font-semibold text-[#3B2523] underline underline-offset-4"
            >
              {CONTACT_EMAIL}
            </a>
          </p>
        )}

        {state !== "checking" && (
          <button
            data-testid="back-home-btn"
            onClick={() => navigate("/")}
            className="mt-9 min-h-[48px] bg-[#3B2523] text-[#F5F1E8] font-semibold px-9 py-4 rounded-full transition-all hover:bg-[#F4D35E] hover:text-[#3B2523]"
          >
            {copy.back}
          </button>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
